import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ReactComponent as Logo } from "../../../../../.MODULS/UCAT_EXAM_MODUL/Assests/download-min.svg";
import { useAppDispatch, useAppSelector } from "../../../../../Redux/Store/Hooks";
import { listInstructionExamID } from "../../../../../Redux/Slices/Exam/ListinstructionExamIDSlice";
import CircularProgress from "@mui/material/CircularProgress";
import { UcatLayout, UcatModal } from "../../../Components/Shared";
import { ScrollWarningModal } from "../../../Components/Practice_Test/Exam_component";

import { listReExamPracticeID } from "../../../../../Redux/Slices/ReExamPractice/ListReExamPracticeSlice";
import { addStudentExam } from "../../../../../Redux/Slices/StudentExam/AddStudentExamSlice";
import { listReExamID } from "../../../../../Redux/Slices/ReExam/ListReExamSlice";
import { decode } from "../../../../../Utils/encodeDecode";
import QuestionMarkIcon from "../../../Assests/green_question_mark.png"
import useNavigationBlocker from "../../../../../Hooks/useNavigationBlocker";


export const UCAT_WELCOME_DATA = {
    examBrand: {
        short: "UCAT",
        full: "University Clinical Aptitude Test",
    },
};

const UcatRetakeTest: React.FC = () => {
    console.info("UcatRetakeTest Loaded");
    useNavigationBlocker(true);


    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useAppDispatch();

    const state = location.state || {};
    const exam_id = state.exam_id || state; // Handle object or direct ID
    const retake_id = state.retake_id;
    const isRetake = state.isRetake;

    const [showBeginExam, setShowBeginExam] = useState(false);

    // Draggable Modal Logic
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const dragStartRef = useRef({ x: 0, y: 0 });

    const handleDragStart = (e: React.MouseEvent) => {
        setIsDragging(true);
        dragStartRef.current = { x: e.clientX - position.x, y: e.clientY - position.y };
    };

    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            if (isDragging) {
                setPosition({
                    x: e.clientX - dragStartRef.current.x,
                    y: e.clientY - dragStartRef.current.y
                });
            }
        };

        const handleMouseUp = () => {
            setIsDragging(false);
        };

        if (isDragging) {
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
        }

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDragging]);
    const [openDialog, setOpenDialog] = useState(false);
    const [showScrollWarning, setShowScrollWarning] = useState(false);
    const [scrollStatus, setScrollStatus] = useState({ left: true, right: true });
    const scrollRef = useRef<HTMLDivElement>(null);

    // Get data from Redux
    const listInstructionExamSlice = useAppSelector(
        (state) => (state as any).ListReExamPracticeIDSlice,
    );
    const examData = listInstructionExamSlice?.data;
    const isLoading = listInstructionExamSlice?.isLoading;

    const scrollStatusRef = useRef(scrollStatus);

    // Update ref when scrollStatus changes
    useEffect(() => {
        scrollStatusRef.current = scrollStatus;
    }, [scrollStatus]);

    const checkScrollStatus = useCallback(() => {
        const buffer = 10;
        let newRight = scrollStatusRef.current.right;

        if (!newRight && scrollRef.current) {
            const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
            if (
                scrollHeight <= clientHeight ||
                scrollTop + clientHeight >= scrollHeight - buffer
            ) {
                newRight = true;
            }
        }

        if (newRight !== scrollStatusRef.current.right) {
            setScrollStatus({ left: true, right: newRight });
        }
    }, []); // No dependencies since we use ref

    const handleScroll = useCallback(() => {
        checkScrollStatus();
    }, [checkScrollStatus]);

    // Check initial scroll status on mount
    useEffect(() => {
        const checkInitial = () => {
            let r = true;
            if (scrollRef.current && scrollRef.current.scrollHeight > scrollRef.current.clientHeight + 5) {
                r = false;
            }
            return { left: true, right: r };
        };

        // Reset scroll position only on initial mount
        if (scrollRef.current) scrollRef.current.scrollTop = 0;

        const initial = checkInitial();
        setScrollStatus(initial);

        // Re-check after a brief delay for content layout
        const timeoutId = setTimeout(() => {
            setScrollStatus(checkInitial());
        }, 300);

        const handleResize = () => {
            if (scrollRef.current) {
                const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
                const buffer = 10;
                const newRight = scrollHeight <= clientHeight || scrollTop + clientHeight >= scrollHeight - buffer;

                if (newRight !== scrollStatusRef.current.right) {
                    setScrollStatus({ left: true, right: newRight });
                }
            }
        };

        window.addEventListener("resize", handleResize);

        return () => {
            clearTimeout(timeoutId);
            window.removeEventListener("resize", handleResize);
        };
    }, []); // Empty dependency array - only run once on mount

    // Fetch exam instruction data on mount
    useEffect(() => {
        const examMode = sessionStorage.getItem("exam_mode");
        const examIdFromStorage = sessionStorage.getItem("exam_id");
        const studentExamIdFromStorage = sessionStorage.getItem("student_exam_id");
        
        if (retake_id || studentExamIdFromStorage) {
            const targetRetakeId = retake_id || studentExamIdFromStorage;
            console.log(`UcatRetakeTest: Loading retake instructions, ID:`, targetRetakeId);
            
            // Only fetch retake instruction data, don't create student exam yet
            dispatch(listReExamPracticeID({ 
                student_test_id: targetRetakeId, 
                client_tz_offset: new Date().getTimezoneOffset() 
            }));
        } else if (exam_id || examIdFromStorage) {
            const targetExamId = exam_id || examIdFromStorage;
            console.log(`UcatRetakeTest: Loading exam instructions, ID:`, targetExamId);
            
            // Fallback to regular exam instructions
            dispatch(listInstructionExamID({ exam_id: targetExamId }));
        } else {
            console.warn("No retake_id or exam_id provided in location.state or sessionStorage");
        }
    }, [dispatch, exam_id, retake_id]);

    const handleNext = () => {
        if (!scrollStatus.left || !scrollStatus.right) {
            setShowScrollWarning(true);
            return;
        }

        setOpenDialog(true);
    };

    const handleConfirmYes = () => {
        // Clear any previous exam session data to ensure a fresh start
        sessionStorage.removeItem("ucat_last_section_index");
        sessionStorage.removeItem("ucat_last_section_id");
        sessionStorage.removeItem("ucat_exam_id");
        sessionStorage.setItem("ucat_exam_mode", "retake");
        
        setOpenDialog(false);
        
        // Navigate to agreement with proper state
        navigate("/ucat_agreement", {
            state: {
                exam_id: exam_id,
                retake_id: retake_id,
                isRetake: true,
                examMode: 'retake',
                startNow: true
            },
        });
    };

    const handleConfirmNo = () => {
        setOpenDialog(false);
    };

    const handleKeyDown = (e: KeyboardEvent) => {
        // Only handle Alt key combinations
        if (e.altKey) {
            const key = e.key.toLowerCase();

            // Prevent default browser behavior
            e.preventDefault();

            if (openDialog) {
                // Dialog context
                switch (key) {
                    case "y":
                        handleConfirmYes();
                        break;
                    case "n":
                        handleConfirmNo();
                        break;
                }
            } else if (showScrollWarning) {
                // Warning context
                if (key === "o") {
                    setShowScrollWarning(false);
                }
            } else {
                // Main screen context
                switch (key) {
                    case "n":
                        handleNext();
                        break;
                }
            }
        }
    };

    useEffect(() => {
        document.addEventListener("keydown", handleKeyDown);

        // Cleanup on unmount
        return () => {
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [handleKeyDown, openDialog, showScrollWarning, handleNext, handleConfirmYes, handleConfirmNo]);

    if (isLoading) {
        return (
            <div className="flex h-screen items-center justify-center bg-white">
                <CircularProgress />
            </div>
        );
    }

    // Use fallback data if examData is not available
    const headerTitle = examData?.data?.exam_name || "UCAT Practice";

    return (
        <>
            <UcatLayout
                headerTitle={headerTitle}
                showToolbar={true}
                footerProps={{
                    onNext: handleNext,
                    leftButtonText: "",
                    rightButtonText: "Next",
                }}
            >
                {/* ================= CONTENT ================= */}
                <div
                    ref={scrollRef}
                    onScroll={handleScroll}
                    className="flex-1 overflow-y-auto ucat-scrollbar min-h-0"
                >
                    <div className="max-w-[100%] mx-auto px-4 py-4">
                        {/* Logo */}
                        <div className="flex justify-end mb-3">
                            <Logo className="w-[456px] h-auto" />
                        </div>

                        {/* Text Content */}
                        <div className="space-y-4 text-[14px] leading-[17px] text-black max-w-full">
                            <div>
                                <p style={{ fontSize: "15px" }}>
                                    Welcome to the UCAT Practice Test.
                                    <br />
                                    <br />
                                    The exam content and introduction screens of these exams are
                                    designed to replicate as much as possible the official UCAT
                                    exam.
                                    <br />
                                    <br />
                                    Apart from this screen, the others you will see are specific
                                    to the live exam. They are provided for your information.
                                    <br />
                                    <br />
                                    Please note:
                                    <br />
                                    <br />
                                    <ul
                                        style={{
                                            listStyleType: "disc",
                                            paddingLeft: "1.5rem",
                                            marginLeft: "4rem",
                                            fontSize: "inherit",
                                        }}
                                    >
                                        <li style={{ fontSize: "inherit" }}>
                                            The practice tests will not save your result or provide a
                                            scale score.
                                        </li>
                                        <li style={{ fontSize: "inherit" }}>
                                            The final review screen (which will not appear in the live
                                            exam) indicates which of your answers are correct /
                                            incorrect.
                                        </li>
                                    </ul>
                                    <br />
                                    <p style={{ fontSize: "inherit", fontWeight: "700" }}>
                                        Note Taking
                                    </p>
                                    <br />
                                    <ul
                                        style={{
                                            listStyleType: "disc",
                                            paddingLeft: "1rem",
                                            marginLeft: "4rem",
                                            fontSize: "inherit",
                                        }}
                                    >
                                        <li style={{ fontSize: "inherit" }}>
                                            {" "}
                                            If you are sitting your test at a test centre, you will be
                                            given a laminated notebook (A4 in size) and a pen.
                                        </li>
                                    </ul>
                                    <br />
                                    <p style={{ fontSize: "inherit", fontWeight: "700" }}>
                                        Calculator
                                    </p>
                                    <br />
                                    <ul
                                        style={{
                                            listStyleType: "disc",
                                            paddingLeft: "1rem",
                                            marginLeft: "4rem",
                                            fontSize: "inherit",
                                        }}
                                    >
                                        <li style={{ fontSize: "inherit" }}>
                                            {" "}
                                            An onscreen calculator is available to assist you in the
                                            test. You can access this by clicking on the button at
                                            the top left of the screen.
                                        </li>
                                        <li style={{ fontSize: "inherit" }}>
                                            {" "}
                                            You can operate the calculator by using the mouse or the
                                            number pad on the keyboard; ensure that 'Num Lock’ is on
                                            for the number pad to work.
                                        </li>
                                        <li style={{ fontSize: "inherit" }}>
                                            {" "}
                                            The calculator opens in a separate window to the test
                                            screen. If you click off the calculator window back to
                                            the test screen (or use a keyboard shortcut, for example
                                            Alt + F to Flag for Review) the calculator will not
                                            function until you click back on it.
                                        </li>
                                    </ul>
                                    <br />
                                    <p style={{ fontSize: "inherit", fontWeight: "700" }}>
                                        Keyboard Shortcuts
                                    </p>
                                    <br />
                                    <ul
                                        style={{
                                            listStyleType: "disc",
                                            paddingLeft: "1rem",
                                            marginLeft: "4rem",
                                            fontSize: "inherit",
                                        }}
                                    >
                                        <li style={{ fontSize: "inherit" }}>
                                            {" "}
                                            Keyboard shortcuts can be used to navigate through the
                                            test if you find this preferable to using the mouse.
                                        </li>
                                        <li style={{ fontSize: "inherit" }}>
                                            {" "}
                                            Wherever there is a letter underlined, the Alt key can be
                                            used with the underlined letter key as a keyboard short
                                            cut. For example, Alt + N = Next.
                                        </li>
                                        <li style={{ fontSize: "inherit" }}>
                                            {" "}
                                            If the standard shortcuts do not work for you in the
                                            practice materials this may be because of the device,
                                            operating system or browser you are using. You are
                                            advised to use the mouse to navigate through the test.
                                        </li>
                                    </ul>
                                    <br />
                                    The 'Navigator' function at the bottom right of the screen
                                    allows you to navigate to questions within the subtest.
                                    <br />
                                    <br />
                                    Please click the{" "}
                                    <span style={{ fontWeight: "700", fontSize: "inherit" }}>
                                        Next (N)
                                    </span>{" "}
                                    button when you are ready to begin the practice test.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ================= CONFIRMATION DIALOG ================= */}
                {openDialog && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center">
                        {/* Custom Modal matching the reference image */}
                        <div
                            className="w-[550px] bg-[#006DAA] border border-white shadow-lg"
                            style={{ transform: `translate(${position.x}px, ${position.y}px)` }}
                        >
                            {/* Header */}
                            <div
                                className="text-white font-normal text-[16px] px-4 py-3 border-b border-white cursor-move select-none"
                                style={{ marginBottom: "16px" }}
                                onMouseDown={handleDragStart}
                            >
                                Ready to Begin Exam
                            </div>

                            {/* Content */}
                            <div className="px-5 pb-3">
                                <div className="flex items-start gap-3">
                                    {/* Green Question Mark Icon */}
                                    <div className="flex-shrink-0">
                                        <div className="w-[32px] h-[32px] rounded-full bg-[#669900] border border-[#7BB800] flex items-center justify-center shadow-md">
                                            <img src={QuestionMarkIcon} style={{ height: "100%", width: "100%" }} alt="?" />
                                        </div>
                                    </div>


                                    <p className="text-white text-[16px] leading-snug pt-1">
                                        If you are ready to begin the exam, select the Yes button.
                                        Otherwise, select the No button to return to the previous screen.
                                    </p>
                                </div>

                                {/* Buttons */}
                                <div className="flex justify-center gap-3 mt-3">
                                    <button
                                        onClick={handleConfirmYes}
                                        className="w-[50px] h-[28px] bg-[#006DAA] text-white text-[16px] font-[400] border border-white hover:color-[#FFFF00] transition-colors leading-none"
                                    >
                                        <span className="underline" style={{ fontSize: "16px" }}>Y</span>es
                                    </button>

                                    <button
                                        onClick={handleConfirmNo}
                                        className="w-[50px] h-[28px] bg-[#006DAA] text-white text-[16px] font-[400] border border-white hover:color-[#FFFF00] transition-colors leading-none"
                                    >
                                        <span className="underline" style={{ fontSize: "16px" }}>N</span>o
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* ================= SCROLL WARNING MODAL ================= */}
                <ScrollWarningModal
                    isOpen={showScrollWarning}
                    onClose={() => setShowScrollWarning(false)}
                />
            </UcatLayout>
        </>
    );
};

export default UcatRetakeTest;
